import { Mail, ArrowRight, MapPin, Globe2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const ROLES = [
  {
    title: 'Senior Full-Stack Engineer (TypeScript / Python)',
    team: 'Engineering',
    location: 'Remote · Africa',
    type: 'Full-time',
    body: 'Ship features end-to-end across our React frontend and FastAPI backend. You\u2019ll work on listings, escrow integrations, and the trust pipeline.',
  },
  {
    title: 'Trust & Safety Analyst',
    team: 'Operations',
    location: 'Accra, Ghana',
    type: 'Full-time',
    body: 'Investigate fraud patterns, review KYC submissions, and mediate disputes. You set the tone for what a fair marketplace feels like.',
  },
  {
    title: 'Dealer Success Manager',
    team: 'Growth',
    location: 'Accra, Ghana',
    type: 'Full-time',
    body: 'Onboard, train, and retain Ghana\u2019s top car dealerships on AutoTrust. Comp includes a performance-based bonus structure.',
  },
];

export default function Careers() {
  return (
    <main data-testid="careers-page">
      <section className="max-w-5xl mx-auto px-4 md:px-8 pt-16 md:pt-24 pb-10">
        <p className="text-xs tracking-[0.18em] uppercase text-gold mb-3 font-medium">Careers</p>
        <h1 className="text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight">
          Build the infrastructure<br className="hidden md:block" /> of West African mobility.
        </h1>
        <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-2xl">
          We&rsquo;re a small, senior team rebuilding how cars are bought and sold in Ghana. We hire generalists who care about craft.
        </p>
        <div className="mt-5 flex flex-wrap gap-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> Accra HQ + remote</span>
          <span className="text-border">·</span>
          <span className="flex items-center gap-1.5"><Globe2 className="w-3 h-3" /> All time zones overlap with West Africa</span>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 md:px-8 py-10 md:py-12 border-t border-border/40">
        <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-6">Open roles</p>
        <div className="space-y-3 md:space-y-4">
          {ROLES.map((r, i) => (
            <a
              key={i}
              href={`mailto:careers@autotrustgh.com?subject=${encodeURIComponent('Application: ' + r.title)}`}
              className="block p-5 md:p-6 rounded-xl border border-border/50 bg-card hover:border-gold/60 transition-colors group"
              data-testid={`role-${i}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="text-lg md:text-xl font-semibold tracking-tight group-hover:text-gold transition-colors">{r.title}</h3>
                  <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground">
                    <Badge variant="secondary" className="font-normal">{r.team}</Badge>
                    <span>·</span>
                    <span>{r.location}</span>
                    <span>·</span>
                    <span>{r.type}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3 max-w-2xl leading-relaxed">{r.body}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-gold group-hover:translate-x-0.5 transition-[transform,color] flex-shrink-0 mt-1" />
              </div>
            </a>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 md:px-8 py-12 md:py-16 border-t border-border/40">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-2">Don&rsquo;t see your role?</h2>
            <p className="text-muted-foreground max-w-md">If you&rsquo;ve shipped something exceptional, we want to hear from you regardless of whether we have an open requisition.</p>
          </div>
          <a href="mailto:careers@autotrustgh.com?subject=General%20application">
            <Button className="bg-gold text-black hover:brightness-110 font-semibold gap-2" data-testid="careers-general-cta">
              <Mail className="w-4 h-4" /> careers@autotrustgh.com
            </Button>
          </a>
        </div>
      </section>
    </main>
  );
}
